import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { ADDRESS_FIELD_ORDER } from "@/constants/base";
import {
  FIELD_KEY_ALIASES,
  TABLE_CONFIG_ID_TO_FIELD_LABEL_ALIASES,
} from "@/constants/propertyFields";
import {
  useReorderPropertyFieldMutation,
  useUpdatePropertyFieldRuleBulkMutation,
  useUpdatePropertyFieldRuleMutation,
} from "@/services/survey-properties";
import {
  useGetTableConfigQuery,
  useGetTeamPropertyLayoutsQuery,
} from "@/services/user";
import { hasAddressLine2AsCustom } from "@/shared/utils";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldModal } from "@emerald-react/modal";
import { EmeraldSearch } from "@emerald-react/search";
import { EmeraldSelect } from "@emerald-react/select";
import { useSortableList } from "@hooks/useSortable";
import { useSurvey } from "@hooks/useSurveySelectors";
import {
  CustomField,
  DetailedSurvey,
  EditColumnsProps,
  PropertyFieldRule,
} from "@survey-types/survey.core.types";
import {
  reorderPropertyField,
  setPropertyFieldRules,
  updatePropertyFieldRule,
  updatePropertyFieldRulesBulk,
} from "@survey/surveySlice";
import { getAddressFieldOrder } from "@utils/index";
import { useEffect, useRef, useState } from "react";
import EditColumnField from "./EditColumnField";
import SortDropdown from "./SortDropdown";

const RESET_LAYOUT_LABEL = "Reset to Default (Team Data)";
const RESET_LAYOUT_VALUE = "RESET_TO_DEFAULT_TEAM_DATA";

const normalizeFieldLabel = (label: unknown): string => {
  if (typeof label !== "string") return "";
  return label.toLowerCase().replace(/[^a-z0-9]/g, "");
};

const normalizeFieldLabelExact = (label: unknown): string => {
  if (typeof label !== "string") return "";
  return label.toLowerCase().trim();
};

const normalizeFieldKey = (key: unknown): string => {
  if (typeof key !== "string") return "";
  return key.toLowerCase().trim();
};

const canonicalFieldKey = (key: unknown): string => {
  const normalized = normalizeFieldLabel(normalizeFieldKey(key));
  return FIELD_KEY_ALIASES[normalized] ?? normalized;
};

const buildPropertyFieldLookupMaps = (fields: PropertyFieldRule[]) => {
  const fieldsByKey = new Map<string, PropertyFieldRule>();
  const fieldsByLabel = new Map<string, PropertyFieldRule>();
  const fieldsByExactLabel = new Map<string, PropertyFieldRule>();

  fields.forEach((field) => {
    const normalizedKey = canonicalFieldKey(field.fieldKey);
    if (normalizedKey && !fieldsByKey.has(normalizedKey)) {
      fieldsByKey.set(normalizedKey, field);
    }

    const normalizedLabel = normalizeFieldLabel(field.fieldLabel);
    if (normalizedLabel && !fieldsByLabel.has(normalizedLabel)) {
      fieldsByLabel.set(normalizedLabel, field);
    }

    const exactLabel = normalizeFieldLabelExact(field.fieldLabel);
    if (exactLabel && !fieldsByExactLabel.has(exactLabel)) {
      fieldsByExactLabel.set(exactLabel, field);
    }
  });

  return { fieldsByKey, fieldsByLabel, fieldsByExactLabel };
};

const getFieldByTableConfigId = (
  tableConfigId: string,
  lookupMaps: {
    fieldsByKey: Map<string, PropertyFieldRule>;
    fieldsByLabel: Map<string, PropertyFieldRule>;
    fieldsByExactLabel: Map<string, PropertyFieldRule>;
  },
  customFields: CustomField[],
) => {
  const normalizedId = normalizeFieldLabel(tableConfigId);
  if (!normalizedId) return undefined;

  const matchedByKey = lookupMaps.fieldsByKey.get(
    canonicalFieldKey(normalizedId),
  );
  if (matchedByKey) return matchedByKey;

  const aliasLabel = TABLE_CONFIG_ID_TO_FIELD_LABEL_ALIASES[normalizedId];
  if (aliasLabel) {
    const matchedByAliasExactLabel = lookupMaps.fieldsByExactLabel.get(
      normalizeFieldLabelExact(aliasLabel),
    );
    if (matchedByAliasExactLabel) return matchedByAliasExactLabel;

    const matchedCustomField = customFields.find(
      (customField) =>
        normalizeFieldLabelExact(customField.originalName) ===
        normalizeFieldLabelExact(aliasLabel),
    );

    if (matchedCustomField?.name) {
      const matchedByCustomFieldNameExact = lookupMaps.fieldsByExactLabel.get(
        normalizeFieldLabelExact(matchedCustomField.name),
      );
      if (matchedByCustomFieldNameExact) return matchedByCustomFieldNameExact;

      const matchedByCustomFieldName = lookupMaps.fieldsByLabel.get(
        normalizeFieldLabel(matchedCustomField.name),
      );
      if (matchedByCustomFieldName) return matchedByCustomFieldName;
    }

    const hasPunctuation = /[^a-z0-9\s]/i.test(aliasLabel);
    if (hasPunctuation) return undefined;

    const matchedByAliasLabel = lookupMaps.fieldsByLabel.get(
      normalizeFieldLabel(aliasLabel),
    );
    if (matchedByAliasLabel) return matchedByAliasLabel;
  }

  return lookupMaps.fieldsByLabel.get(normalizedId);
};

const buildFieldRuleUpdatesFromTableConfig = (
  tableConfig: Array<{ id: string; isActive: boolean }>,
  propertyFields: PropertyFieldRule[],
  customFields: CustomField[],
  isAddressLine2Custom: boolean,
) => {
  type FieldRuleUpdate = {
    fieldId: string;
    isHidden: boolean;
    status: "ACTIVE" | "INACTIVE";
    order: number;
    fieldKey: string;
    label: string;
  };

  const lookupMaps = buildPropertyFieldLookupMaps(propertyFields);
  const adjustedAddressFieldOrder = isAddressLine2Custom
    ? ADDRESS_FIELD_ORDER.filter((key) => key !== "address2")
    : ADDRESS_FIELD_ORDER;

  const matchedFieldIds = new Set<string>();

  const matchedTableConfig = tableConfig.filter((rule) =>
    getFieldByTableConfigId(rule.id, lookupMaps, customFields),
  );
  const matchedUpdates: FieldRuleUpdate[] = matchedTableConfig.map((rule) => {
    const field = getFieldByTableConfigId(rule.id, lookupMaps, customFields)!;
    const isActive = Boolean(rule.isActive);
    matchedFieldIds.add(field.fieldId);

    return {
      fieldId: field.fieldId,
      isHidden: !isActive,
      status: isActive ? ("ACTIVE" as const) : ("INACTIVE" as const),
      order: 0,
      fieldKey: field.fieldKey,
      label: field.fieldLabel,
    };
  });

  const matchedUpdatesByFieldId = new Map(
    matchedUpdates.map((update) => [update.fieldId, update]),
  );

  // Keep address fields pinned in canonical order. If Address Line 2 is not custom,
  // ensure it is included even when absent in the selected table config.
  const orderedAddressUpdates: FieldRuleUpdate[] = adjustedAddressFieldOrder
    .map((addressKey) => {
      const propertyField = propertyFields.find(
        (f) => f.fieldKey === addressKey,
      );
      if (!propertyField) return undefined;

      const existingUpdate = matchedUpdatesByFieldId.get(propertyField.fieldId);
      if (existingUpdate) return existingUpdate;

      const fallbackIsHidden = Boolean(propertyField.isHidden);
      matchedFieldIds.add(propertyField.fieldId);

      return {
        fieldId: propertyField.fieldId,
        isHidden: fallbackIsHidden,
        status: fallbackIsHidden ? ("INACTIVE" as const) : ("ACTIVE" as const),
        order: 0,
        fieldKey: propertyField.fieldKey,
        label: propertyField.fieldLabel,
      };
    })
    .filter((update): update is FieldRuleUpdate => Boolean(update));

  const orderedAddressFieldIds = new Set(
    orderedAddressUpdates.map((update) => update.fieldId),
  );

  const nonAddressUpdates = matchedUpdates.filter(
    (update) => !orderedAddressFieldIds.has(update.fieldId),
  );

  const orderedMatchedUpdates = [
    ...orderedAddressUpdates,
    ...nonAddressUpdates,
  ].map((update, orderIndex) => ({
    fieldId: update.fieldId,
    isHidden: update.isHidden,
    status: update.status,
    order: orderIndex,
    label: update.label,
  }));

  const unmatchedUpdates = propertyFields
    .filter((field) => !matchedFieldIds.has(field.fieldId))
    .map((field, index) => ({
      fieldId: field.fieldId,
      isHidden: true,
      status: "INACTIVE" as const,
      order: orderedMatchedUpdates.length + index,
      label: field.fieldLabel,
    }));

  return [...orderedMatchedUpdates, ...unmatchedUpdates];
};

const EditColumns = ({ isOpen, onClose }: EditColumnsProps) => {
  const [reorderPropertyFieldApi] = useReorderPropertyFieldMutation();
  const [updatePropertyFieldRuleApi] = useUpdatePropertyFieldRuleMutation();
  const [updatePropertyFieldRuleBulkApi, { isLoading: isApplyingLayout }] =
    useUpdatePropertyFieldRuleBulkMutation();
  const surveyId = useAppSelector((state) => state.survey?.currentSurvey?.id);
  const dispatch = useAppDispatch();
  const currentSurvey = useSurvey();
  const email = localStorage.getItem("email") ?? "";
  const { data: userResponse } = useGetTableConfigQuery(
    { email: email },
    { skip: !email },
  );
  const teamTableConfig = userResponse?.currentUser?.tableConfig;
  const customFields = currentSurvey?.customFields ?? [];
  const { data: savedLayoutsRaw = [], isLoading: isLayoutsLoading } =
    useGetTeamPropertyLayoutsQuery(
      { teamId: currentSurvey?.teamId ?? "" },
      { skip: !currentSurvey?.teamId },
    );
  const savedLayouts = [...savedLayoutsRaw].sort((a, b) => {
    const aDate = a.createdOn ? new Date(a.createdOn).getTime() : 0;
    const bDate = b.createdOn ? new Date(b.createdOn).getTime() : 0;
    return bDate - aDate;
  });

  let dragDisabledItems = [
    "name",
    "address1",
    "address2",
    "city",
    "stateProvince",
    "postalCode",
  ];

  const [searchValue, setSearchValue] = useState("");
  const [layoutName, setLayoutName] = useState("");
  const rightPanelListRef = useRef<HTMLDivElement | null>(null);

  const propertyFields = useAppSelector(
    (state) =>
      state.survey?.currentSurvey?.surveyPropertyFieldRule
        .propertyFieldRuleList,
  );

  const activePropertyFields = useAppSelector(
    (state) =>
      state.survey?.currentSurvey?.surveyPropertyFieldRule
        ?.activePropertyFieldRuleList,
  );
  const inactivePropertyFields = useAppSelector(
    (state) =>
      state.survey?.currentSurvey?.surveyPropertyFieldRule
        ?.inactivePropertyFieldRuleList,
  );

  const sortableActiveFields = !activePropertyFields
    ? []
    : [...activePropertyFields].map((field) => ({
        ...field,
        id: field.fieldId,
      }));

  const sortableInactiveFields = !inactivePropertyFields
    ? []
    : [...inactivePropertyFields].map((field) => ({
        ...field,
        id: field.fieldId,
      }));

  const isAddressLine2Custom = hasAddressLine2AsCustom(
    currentSurvey as DetailedSurvey,
  );

  if (isAddressLine2Custom) {
    dragDisabledItems = dragDisabledItems.filter((item) => item !== "address2");
  }

  const visibleStaticFields = sortableActiveFields
    .filter(
      (field) => !field.isHidden && dragDisabledItems.includes(field.fieldKey),
    )
    .sort((a, b) => a.order - b.order);

  const visibleFields = sortableActiveFields
    .filter((f) => !f.isHidden && !dragDisabledItems.includes(f.fieldKey))
    .sort((a, b) => a.order - b.order);

  const hiddenFields = sortableInactiveFields.sort((a, b) => {
    const aIsAddress = dragDisabledItems.includes(a.fieldKey);
    const bIsAddress = dragDisabledItems.includes(b.fieldKey);
    if (aIsAddress && !bIsAddress) return -1;
    if (!aIsAddress && bIsAddress) return 1;
    if (aIsAddress && bIsAddress) {
      return (
        dragDisabledItems.indexOf(a.fieldKey) -
        dragDisabledItems.indexOf(b.fieldKey)
      );
    }
    return a.order - b.order;
  });

  const previousVisibleCountRef = useRef(visibleFields.length);
  const previousVisibleStaticCountRef = useRef(visibleStaticFields.length);

  useEffect(() => {
    if (
      !isApplyingLayout &&
      visibleFields.length > previousVisibleCountRef.current
    ) {
      rightPanelListRef.current?.scrollTo({
        top: rightPanelListRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
    previousVisibleCountRef.current = visibleFields.length;
  }, [visibleFields.length, isApplyingLayout]);

  useEffect(() => {
    if (
      !isApplyingLayout &&
      visibleStaticFields.length > previousVisibleStaticCountRef.current
    ) {
      rightPanelListRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    previousVisibleStaticCountRef.current = visibleStaticFields.length;
  }, [visibleStaticFields.length, isApplyingLayout]);

  const [sortedFields, setSortedFields] =
    useState<PropertyFieldRule[]>(hiddenFields);

  const hiddenFieldsChangeKey = hiddenFields
    .map(
      (field) =>
        `${field.fieldId}:${field.fieldLabel}:${field.fieldKey}:${field.order}:${field.isHidden}:${field.status}`,
    )
    .join("|");

  useEffect(() => {
    setSortedFields(hiddenFields);
  }, [hiddenFieldsChangeKey]);

  // Sync layoutName only when the modal first opens or savedLayouts first loads.
  // We do NOT watch currentSurvey?.layoutId because the backend may update it
  // long after the user selects a layout, causing the displayed name to flicker
  // back to the previous value and then snap forward again.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!justOpened) return;

    const allLayoutOptions = [
      ...savedLayouts.map((layout) => ({
        value: layout.layoutId,
        layoutId: layout.layoutId,
      })),
      { value: RESET_LAYOUT_VALUE, layoutId: RESET_LAYOUT_VALUE },
    ];

    const selectedLayout = allLayoutOptions.find(
      (layout) => layout.layoutId === currentSurvey?.layoutId,
    );

    setLayoutName(selectedLayout?.value ?? "");
  }, [isOpen, savedLayouts, currentSurvey?.layoutId]);

  const clearSelectedLayoutOnManualChange = () => {
    setLayoutName("");
  };

  const handleMoveToVisible = (field: PropertyFieldRule) => {
    clearSelectedLayoutOnManualChange();
    let order = activePropertyFields?.length;
    if (dragDisabledItems.includes(field.fieldKey)) {
      order = getAddressFieldOrder(
        field.fieldKey,
        activePropertyFields ?? [],
        activePropertyFields ?? [],
        inactivePropertyFields ?? [],
        isAddressLine2Custom,
      );
      const activeFieldRule = [
        ...(currentSurvey?.surveyPropertyFieldRule
          ?.activePropertyFieldRuleList ?? []),
      ];
      activeFieldRule.splice(order, 0, {
        fieldId: field.fieldId,
        fieldKey: field.fieldKey,
        fieldLabel: field.fieldLabel,
        isHidden: false,
        order: order,
        status: "ACTIVE",
      });
      const modifiedActiveFieldRule = activeFieldRule.map((f, i) => ({
        ...f,
        order: i,
      }));
      const inactiveFieldRule =
        currentSurvey?.surveyPropertyFieldRule?.inactivePropertyFieldRuleList.filter(
          (f) => f.fieldId !== field.fieldId,
        );
      dispatch(
        setPropertyFieldRules({
          surveyId: surveyId ?? "",
          propertyFieldRuleList:
            currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList ?? [],
          activePropertyFieldRuleList: modifiedActiveFieldRule ?? [],
          inactivePropertyFieldRuleList: inactiveFieldRule ?? [],
        }),
      );
    } else {
      dispatch(
        updatePropertyFieldRule({
          fieldId: field.fieldId,
          isHidden: false,
          order: order ?? 0,
          status: "ACTIVE",
        }),
      );
    }
    updatePropertyFieldRuleApi({
      surveyId: surveyId ?? "",
      fieldRuleUpdates: {
        fieldId: field.fieldId,
        isHidden: false,
        order: order ?? 0,
        status: "ACTIVE",
      },
      propertyLayoutId: "",
    });
  };

  const handleMoveToHidden = (field: PropertyFieldRule) => {
    clearSelectedLayoutOnManualChange();
    let order = inactivePropertyFields?.length;
    if (dragDisabledItems.includes(field.fieldKey)) {
      order = getAddressFieldOrder(
        field.fieldKey,
        inactivePropertyFields ?? [],
        activePropertyFields ?? [],
        inactivePropertyFields ?? [],
        isAddressLine2Custom,
      );
      const activeFieldRule =
        currentSurvey?.surveyPropertyFieldRule?.activePropertyFieldRuleList.filter(
          (f) => f.fieldId !== field.fieldId,
        );
      const inactiveFieldRule = [
        ...(currentSurvey?.surveyPropertyFieldRule
          ?.inactivePropertyFieldRuleList ?? []),
      ];
      inactiveFieldRule.splice(order, 0, {
        fieldId: field.fieldId,
        fieldKey: field.fieldKey,
        fieldLabel: field.fieldLabel,
        isHidden: true,
        order: order,
        status: "INACTIVE",
      });
      const modifiedInactiveFieldRule = inactiveFieldRule.map((f, i) => ({
        ...f,
        order: i,
      }));
      dispatch(
        setPropertyFieldRules({
          surveyId: surveyId ?? "",
          propertyFieldRuleList:
            currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList ?? [],
          activePropertyFieldRuleList: activeFieldRule ?? [],
          inactivePropertyFieldRuleList: modifiedInactiveFieldRule ?? [],
        }),
      );
    } else {
      dispatch(
        updatePropertyFieldRule({
          fieldId: field.fieldId,
          isHidden: true,
          order: order ?? 0,
          status: "INACTIVE",
        }),
      );
    }

    updatePropertyFieldRuleApi({
      surveyId: surveyId ?? "",
      fieldRuleUpdates: {
        fieldId: field.fieldId,
        isHidden: true,
        order: order ?? 0,
        status: "INACTIVE",
      },
      propertyLayoutId: "",
    });
  };

  const handleVisibleReorder = (
    _reorderedFields: PropertyFieldRule[],
    sourceIndex: number,
    destinationIndex: number,
  ) => {
    if (!propertyFields || !activePropertyFields) return;
    clearSelectedLayoutOnManualChange();

    // sourceIndex/destinationIndex are indices within visibleFields (draggable subset),
    // not within the full activePropertyFieldRuleList — translate to global indices.
    const sourceFieldId = visibleFields[sourceIndex].fieldId;
    const destFieldId = visibleFields[destinationIndex].fieldId;

    const globalSourceIndex = activePropertyFields.findIndex(
      (f) => f.fieldId === sourceFieldId,
    );
    const globalDestIndex = activePropertyFields.findIndex(
      (f) => f.fieldId === destFieldId,
    );
    const globalPropertyDestIndex = propertyFields.findIndex(
      (f) => f.fieldId === destFieldId,
    );

    if (globalSourceIndex === -1 || globalDestIndex === -1) return;

    dispatch(
      reorderPropertyField({
        sourceIndex: globalSourceIndex,
        destinationIndex: globalDestIndex,
      }),
    );
    reorderPropertyFieldApi({
      surveyId: surveyId || "",
      order: globalPropertyDestIndex,
      fieldId: sourceFieldId,
      propertyLayoutId: "",
    });
  };

  const {
    DndContextProps: visibleDndProps,
    SortableContextProps: visibleSortableProps,
  } = useSortableList({
    items: visibleFields,
    onReorder: handleVisibleReorder,
    idField: "fieldId",
  });

  if (!isOpen) return null;

  const sortOptionHandler = (value: string) => {
    if (value === "A to Z") {
      const sorted = [...hiddenFields].sort((a, b) =>
        a.fieldLabel.localeCompare(b.fieldLabel),
      );
      setSortedFields(sorted);
    } else if (value === "Z to A") {
      const sorted = [...hiddenFields].sort((a, b) =>
        b.fieldLabel.localeCompare(a.fieldLabel),
      );
      setSortedFields(sorted);
    } else {
      setSortedFields(hiddenFields);
    }
  };

  const handleLayoutSelect = (_event: unknown, value: string | string[]) => {
    const layoutId = Array.isArray(value) ? value[0] : value;

    if (!layoutId || !surveyId || !propertyFields?.length) return;

    const applyFieldRuleUpdates = (
      fieldRuleUpdates: Array<{
        fieldId: string;
        isHidden: boolean;
        status: "ACTIVE" | "INACTIVE";
        order: number;
      }>,
    ) => {
      dispatch(updatePropertyFieldRulesBulk(fieldRuleUpdates));
      updatePropertyFieldRuleBulkApi({
        surveyId,
        fieldRuleUpdates,
        propertyLayoutId: layoutId,
      });
    };

    if (layoutId === RESET_LAYOUT_VALUE) {
      if (!teamTableConfig) return;

      setLayoutName(layoutId);
      const fieldRuleUpdates = buildFieldRuleUpdatesFromTableConfig(
        teamTableConfig,
        propertyFields,
        customFields,
        isAddressLine2Custom,
      );

      applyFieldRuleUpdates(fieldRuleUpdates);
      return;
    }

    const selectedLayout = savedLayouts.find(
      (layout) => layout.layoutId === layoutId,
    );
    if (!selectedLayout) return;

    setLayoutName(layoutId);

    const fieldRuleUpdates = buildFieldRuleUpdatesFromTableConfig(
      selectedLayout.tableConfig,
      propertyFields,
      customFields,
      isAddressLine2Custom,
    );

    applyFieldRuleUpdates(fieldRuleUpdates);
  };

  const clearAllColumns = () => {
    clearSelectedLayoutOnManualChange();
    const visibleAllFields =
      propertyFields?.filter((field) => !field.isHidden) ?? [];

    const visibleAddressFields = visibleAllFields
      .filter((field) => dragDisabledItems.includes(field.fieldKey))
      .sort((a, b) => {
        const aIndex = dragDisabledItems.indexOf(a.fieldKey);
        const bIndex = dragDisabledItems.indexOf(b.fieldKey);
        const safeAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const safeBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        return safeAIndex - safeBIndex;
      });

    const visibleAddressFieldIds = new Set(
      visibleAddressFields.map((field) => field.fieldId),
    );

    const visibleNonAddressFields = visibleAllFields.filter(
      (field) => !visibleAddressFieldIds.has(field.fieldId),
    );

    const orderedHiddenFieldsAfterClear = [
      ...visibleAddressFields,
      ...hiddenFields,
      ...visibleNonAddressFields,
    ];

    const clearedFields = orderedHiddenFieldsAfterClear.map((field, index) => ({
      fieldId: field.fieldId,
      isHidden: true,
      status: "INACTIVE" as const,
      order: index,
    }));

    if (clearedFields && surveyId) {
      dispatch(updatePropertyFieldRulesBulk(clearedFields));
      updatePropertyFieldRuleBulkApi({
        surveyId: surveyId,
        fieldRuleUpdates: clearedFields,
        propertyLayoutId: "",
      });
    }
  };

  const handleSearchChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    setSearchValue(value);
  };

  const handleSearchClear = () => {
    setSearchValue("");
  };

  return (
    <EmeraldModal
      opened={isOpen}
      onClose={onClose}
      closeOnOutsideClick={true}
      zIndex={1200}
      title="Edit Columns"
      headerText="Edit Columns"
      hasCloseIcon={true}
      className="edit-columns-modal"
    >
      <p className="edit-columns-modal__instructions">
        Click from the Available Columns section to customize your Column Layout
        and rearrange the fields in your preferred order.
      </p>
      <div className="edit-columns-modal__body">
        <div className="edit-columns-modal__panel mr-4">
          <div className="panel-header">Available Columns</div>
          <div className="edit-columns-modal__panel-list border rounded-md overflow-hidden flex flex-col">
            <div className="edit-search-filters">
              <EmeraldSearch
                placeholder={"Search"}
                showSuggestions={true}
                maxSuggestions={5}
                value={searchValue}
                onChange={handleSearchChange}
                className="m-4 edit-search"
                onClear={handleSearchClear}
              />
              <div className="edit-filters">
                <SortDropdown onSortOptionChange={sortOptionHandler} />
              </div>
            </div>
            <div className="edit-columns-modal__panel-list-content flex-1 min-h-0 overflow-y-auto">
              {hiddenFields.length === 0 ? (
                <div className="edit-columns-modal__empty">
                  All columns are visible
                </div>
              ) : (
                <>
                  {sortedFields
                    .filter((field) =>
                      field.fieldLabel
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()),
                    )
                    .map((field) => (
                      <EditColumnField
                        key={field.fieldId}
                        field={field}
                        isDragDisabled={dragDisabledItems.includes(
                          field.fieldKey,
                        )}
                        side="left"
                        onToggle={() => handleMoveToVisible(field)}
                      />
                    ))}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="edit-columns-modal__panel">
          <div className="header flex justify-between items-center">
            <div className="panel-header">Column Layout</div>
            <div
              className="clearColumns text-xs text-primary font-semibold cursor-pointer"
              onClick={clearAllColumns}
            >
              CLEAR ALL
            </div>
          </div>
          <div className="relative">
            <EmeraldSelect
              label={"Select Column Layout"}
              placeholder={
                isLayoutsLoading
                  ? "Loading layouts..."
                  : isApplyingLayout
                    ? "Applying layout..."
                    : "Select a saved layout"
              }
              title={"Emerald"}
              value={layoutName}
              editable={false}
              outlined
              options={[
                ...savedLayouts.map((layout) => ({
                  value: layout.layoutId,
                  label: layout.layoutName,
                })),
                {
                  value: RESET_LAYOUT_VALUE,
                  label: RESET_LAYOUT_LABEL,
                },
              ]}
              disabled={isLayoutsLoading || isApplyingLayout}
              onChange={handleLayoutSelect}
            />
            {isApplyingLayout && (
              <div className="absolute right-10 top-1/2 -translate-y-1/2 flex items-center">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
          </div>
          <div
            className="edit-columns-modal__panel-list border rounded-md overflow-y-auto mt-3"
            ref={rightPanelListRef}
          >
            {visibleFields.length === 0 && visibleStaticFields.length === 0 ? (
              <div className="edit-columns-modal__empty">
                No columns are visible
              </div>
            ) : (
              <>
                {visibleStaticFields.map((field) => (
                  <EditColumnField
                    key={field.fieldId}
                    field={field}
                    side="right"
                    isDragDisabled={dragDisabledItems.includes(field.fieldKey)}
                    onToggle={() => handleMoveToHidden(field)}
                  />
                ))}
                <DndContext {...visibleDndProps}>
                  <SortableContext {...visibleSortableProps}>
                    {visibleFields.map((field) => (
                      <EditColumnField
                        key={field.fieldId}
                        field={field}
                        side="right"
                        onToggle={() => handleMoveToHidden(field)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </>
            )}
          </div>
        </div>
      </div>
    </EmeraldModal>
  );
};

export default EditColumns;
